import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Link,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, VpnKey as KeyIcon } from '@mui/icons-material';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { PagedDoc } from '../components/PagedDoc';

/**
 * Renders API.md, which ships in public/ so it is part of the build.
 *
 * Shown one section at a time — an endpoint per page — with contents, links and search by
 * PagedDoc, shared with the User Guide. At 1,600 lines the whole document on one screen was
 * the hardest page in the app to find anything on. Links take the form
 * `/api-documentation#patch-api-v1-posts-id/taking-a-post-off-the-schedule-and-putting-it-back`.
 */

// The LinkedIn reviewer is sent to the Dashboard (2026-10-17, Ian's ask): it cannot create keys,
// and the developer API is not part of what LinkedIn is reviewing.
export const ApiDocs: React.FC = () => {
  const { user } = useAuth();
  return user?.role === 'demo' ? <Navigate to="/" replace /> : <ApiDocsContent />;
};

const ApiDocsContent: React.FC = () => {
  const navigate = useNavigate();
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/API.md')
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.text();
      })
      .then(text => {
        // A missing file would be served as index.html by the SPA fallback
        if (text.trimStart().startsWith('<')) {
          throw new Error('not found');
        }
        setSource(text);
      })
      .catch(() => setError('Could not load the API documentation.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" component="h1">
          API Documentation
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<KeyIcon />}
            onClick={() => navigate('/settings')}
          >
            Create an API key
          </Button>
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            component="a"
            href="/API.md"
            target="_blank"
            rel="noopener noreferrer"
          >
            Raw markdown
          </Button>
        </Box>
      </Box>

      <Alert severity="info" icon={<KeyIcon />} sx={{ mb: 3 }}>
        Every request needs an API key. Create one under{' '}
        <Link component="button" onClick={() => navigate('/settings')} sx={{ verticalAlign: 'baseline' }}>
          Settings → API Keys
        </Link>
        {' '}— the key is shown once and cannot be retrieved afterwards.
      </Alert>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <PagedDoc source={source} basePath="/api-documentation" noun="the API docs" />
      )}
    </Box>
  );
};
